import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

// ページネーションで表示する要素の型
type PaginationElement = number | "ellipsis";

/**
 * ページネーションで表示する要素を返す
 */
const generatePaginationItems = ({
  page,
  totalPages,
}: {
  page: number;
  totalPages: number;
}): PaginationElement[] => {
  const paginationItems: PaginationElement[] = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(i);
    }
  } else {
    if (page < 4) {
      for (let i = 1; i <= 5; i++) {
        paginationItems.push(i);
      }
      paginationItems.push("ellipsis");
      paginationItems.push(totalPages);
    } else if (page > totalPages - 3) {
      paginationItems.push(1);
      paginationItems.push("ellipsis");
      for (let i = totalPages - 4; i <= totalPages; i++) {
        paginationItems.push(i);
      }
    } else {
      paginationItems.push(1);
      paginationItems.push("ellipsis");
      for (let i = page - 1; i <= page + 1; i++) {
        paginationItems.push(i);
      }
      paginationItems.push("ellipsis");
      paginationItems.push(totalPages);
    }
  }

  return paginationItems;
};

interface FacilityPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const FacilityPagination = ({
  currentPage,
  totalPages,
  onPageChange,
}: FacilityPaginationProps) => {
  if (totalPages <= 1) return null;

  return (
    <div className="mt-0">
      <Pagination>
        <PaginationContent className="w-full flex-col sm:flex-row">
          {/* Nav buttons for small screens (mobile) shown at top */}
          <div className="mb-4 flex w-full justify-between sm:hidden">
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(currentPage - 1);
                }}
                className={cn("transition hover:text-primary-5", {
                  "pointer-events-none text-muted": currentPage === 1,
                })}
              >
                前へ
              </PaginationPrevious>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(currentPage + 1);
                }}
                className={cn("transition hover:text-primary-5", {
                  "pointer-events-none text-muted": currentPage >= totalPages,
                })}
              >
                次へ
              </PaginationNext>
            </PaginationItem>
          </div>

          {/* Page numbers - centered on mobile, left-aligned on desktop */}
          <div className="flex flex-row flex-wrap justify-center sm:ml-4 sm:justify-start md:gap-1">
            {generatePaginationItems({
              page: currentPage,
              totalPages,
            }).map((item, i) => (
              <PaginationItem key={i}>
                {item === "ellipsis" ? (
                  <PaginationEllipsis />
                ) : (
                  <PaginationLink
                    isActive={currentPage === item}
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(item);
                    }}
                    href="#"
                    className={cn(
                      "rounded-none text-primary-5 transition hover:text-primary-5",
                      {
                        "border-b border-b-primary-3 text-primary-5 hover:text-primary-6":
                          currentPage === item,
                      },
                    )}
                  >
                    {item.toString(10).padStart(2, "0")}
                  </PaginationLink>
                )}
              </PaginationItem>
            ))}
          </div>

          {/* Nav buttons for desktop - shown at right */}
          <div className="ml-auto hidden sm:flex">
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(currentPage - 1);
                }}
                className={cn(
                  "text-primary-5 transition hover:text-primary-5",
                  {
                    "pointer-events-none text-muted": currentPage === 1,
                  },
                )}
              >
                前へ
              </PaginationPrevious>
            </PaginationItem>
            <div className="mx-4 h-6 w-px self-center bg-monotone-3"></div>
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(currentPage + 1);
                }}
                className={cn(
                  "text-primary-5 transition hover:text-primary-6",
                  {
                    "pointer-events-none text-muted": currentPage >= totalPages,
                  },
                )}
              >
                次へ
              </PaginationNext>
            </PaginationItem>
          </div>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
